import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import API from '../../../services/API'
import PageHeader from '../../kit/PageHeader'
import SectionCard from '../../kit/SectionCard'
import {
  HARM_OPTIONS,
  MODULE_OPTIONS,
  PLATFORM_OPTIONS,
  SOURCE_OPTIONS,
  YES_NO_UNKNOWN,
} from './constants'

const nowLocal = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Manual entry for incidents that arrive by phone, email, live chat or from
// a customer's governance team rather than through the in-app ticket.
const NewSafetyIncident = () => {
  const navigate = useNavigate()
  const [tenants, setTenants] = useState([])
  const [tenant, setTenant] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    source: 'Phone',
    reporterName: '',
    reporterEmail: '',
    reporterPhone: '',
    reportedAt: nowLocal(),
    occurredAt: '',
    module: '',
    platform: 'Unknown',
    softwareVersion: '',
    title: '',
    description: '',
    immediateActions: '',
    isOngoing: 'Unknown',
    reporterHarm: 'Unknown',
    patientsAffected: '',
    recordReferences: '',
  })

  useEffect(() => {
    API.get('/admincrm/clinical-risk/safety-incidents/tenant-options').then((res) => {
      if (res.status === 200) setTenants(res.data || [])
    })
  }, [])

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const canSave = form.title.trim() && form.description.trim() && !saving

  const save = () => {
    setSaving(true)
    setError('')
    API.post('/admincrm/clinical-risk/safety-incidents', {
      ...form,
      tenant: tenant ? { tenantId: tenant._id } : undefined,
      reporter: {
        name: form.reporterName,
        email: form.reporterEmail,
        phone: form.reporterPhone,
      },
      recordReferences: form.recordReferences
        .split(/[\n,;]+/)
        .map((x) => x.trim())
        .filter(Boolean),
    })
      .then((res) => {
        if (res.status === 201) navigate(`/safety-incidents/${res.data._id}`)
        else setError(res.data?.error || 'Could not log the incident.')
      })
      .catch(() => setError('Could not log the incident.'))
      .finally(() => setSaving(false))
  }

  return (
    <Stack gap={2.5}>
      <PageHeader
        eyebrow="Clinical safety"
        title="Log a safety incident"
        subtitle="For reports that did not come through the in-app ticket. The entry is acknowledged as you save it; triage, investigation and hazard linkage happen on the incident page."
        back="/safety-incidents"
        actions={
          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={save}
            disabled={!canSave}
          >
            {saving ? 'Saving…' : 'Log incident'}
          </Button>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <SectionCard title="What was reported">
            <Stack gap={2}>
              <TextField
                label="Title (one line)"
                value={form.title}
                onChange={set('title')}
                required
                fullWidth
              />
              <TextField
                label="What happened"
                helperText="As reported. No patient identifiers; record references only."
                value={form.description}
                onChange={set('description')}
                required
                multiline
                minRows={5}
                fullWidth
              />
              <TextField
                label="Immediate actions taken"
                value={form.immediateActions}
                onChange={set('immediateActions')}
                multiline
                minRows={2}
                fullWidth
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Module" value={form.module} onChange={set('module')} fullWidth>
                    <MenuItem value="">Not specified</MenuItem>
                    {MODULE_OPTIONS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Platform" value={form.platform} onChange={set('platform')} fullWidth>
                    {PLATFORM_OPTIONS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Reporter's harm assessment"
                    value={form.reporterHarm}
                    onChange={set('reporterHarm')}
                    fullWidth
                  >
                    {HARM_OPTIONS.map((h) => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Still happening?" value={form.isOngoing} onChange={set('isOngoing')} fullWidth>
                    {YES_NO_UNKNOWN.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    label="Patients affected (approx.)"
                    value={form.patientsAffected}
                    onChange={set('patientsAffected')}
                    inputProps={{ min: 0 }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Record references"
                    helperText="ePCR, booking or CAD refs, comma separated"
                    value={form.recordReferences}
                    onChange={set('recordReferences')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="datetime-local"
                    label="Occurred at"
                    value={form.occurredAt}
                    onChange={set('occurredAt')}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Software version"
                    value={form.softwareVersion}
                    onChange={set('softwareVersion')}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionCard title="Who reported it">
            <Stack gap={2}>
              <Autocomplete
                options={tenants}
                value={tenant}
                onChange={(e, value) => setTenant(value)}
                getOptionLabel={(option) => `${option.name} (${option.subdomain})`}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                renderInput={(params) => <TextField {...params} label="Tenant" />}
              />
              <TextField select label="Source" value={form.source} onChange={set('source')} fullWidth>
                {SOURCE_OPTIONS.filter((s) => !s.endsWith('ticket')).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Reporter name" value={form.reporterName} onChange={set('reporterName')} fullWidth />
              <TextField label="Reporter email" value={form.reporterEmail} onChange={set('reporterEmail')} fullWidth />
              <TextField label="Reporter phone" value={form.reporterPhone} onChange={set('reporterPhone')} fullWidth />
              <TextField
                type="datetime-local"
                label="Reported at"
                helperText="When AmbuNet first learned of it; the CRMP targets count from here"
                value={form.reportedAt}
                onChange={set('reportedAt')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>
    </Stack>
  )
}

export default NewSafetyIncident
